import type { CSSProperties, ReactNode, RefObject } from 'react';
import { CheckCircle2 } from 'lucide-react';

type LoginEmailStepProps = {
  emailInputRef: RefObject<HTMLInputElement | null>;
  email: string;
  onEmailChange: (value: string) => void;
  containerStyle?: CSSProperties;
  loginStepErrorCard: ReactNode;
  showLoginStepErrorCard: boolean;
  showLegacyInlineLoginError: boolean;
  error: string;
  overlayInputStyle: CSSProperties;
  loginFieldShellStyle: CSSProperties;
  emailInputPaddingRight?: number;
  emailLooksValid: boolean;
  currentEmailStatus: string;
  onForgotEmailLookup: () => void;
};

export function LoginEmailStep({
  emailInputRef,
  email,
  onEmailChange,
  containerStyle,
  loginStepErrorCard,
  showLoginStepErrorCard,
  showLegacyInlineLoginError,
  error,
  overlayInputStyle,
  loginFieldShellStyle,
  emailInputPaddingRight,
  emailLooksValid,
  currentEmailStatus,
  onForgotEmailLookup,
}: LoginEmailStepProps) {
  return (
    <>
      {/*#region AC_BLOCK login.email-step -- Identificacao de conta*/}
      {/* @ac-purpose Renderiza o primeiro passo do login oficial: e-mail antes da senha. */}
      {/* @ac-related docs_secutiry/acesso/login-unico-inventario.md#LoginPage */}
      {/* @ac-related react-app/src/pages/auth/LoginPage.tsx#renderEmailOverlay */}
      {/* @ac-routes /login */}
      {/* @ac-tests node tools/codex-kit.mjs check react-tsc */}
      <div style={{ position: 'relative', margin: 0, ...containerStyle }}>
        {loginStepErrorCard}
        <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
          <div style={{ position: 'relative' }}>
            <input
              ref={emailInputRef}
              data-testid="login-email-input"
              className="ac-login-email-input"
              type="email"
              id="username"
              placeholder="Entre com seu e-mail"
              name="username"
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
              enterKeyHint="next"
              required
              value={email}
              autoFocus
              tabIndex={showLoginStepErrorCard ? -1 : undefined}
              onChange={(e) => {
                e.currentTarget.setCustomValidity('');
                onEmailChange(e.target.value);
              }}
              style={{
                ...overlayInputStyle,
                paddingRight: emailInputPaddingRight,
              }}
            />
            {emailLooksValid && currentEmailStatus === 'exists' && (
              <div
                aria-label="E-mail confirmado"
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(34, 197, 94, 0.13)',
                  color: '#16a34a',
                  boxShadow: '0 8px 18px rgba(22, 163, 74, 0.18)',
                }}
              >
                <CheckCircle2 size={18} />
              </div>
            )}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginTop: 12,
          }}>
            <button
              type="button"
              onClick={onForgotEmailLookup}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a78bfa',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 500,
                padding: '0 0 0 10px',
                opacity: 0.6,
                whiteSpace: 'nowrap',
                textShadow: '0 0 14px rgba(196,181,253,0.42)',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
                e.currentTarget.style.opacity = '0.6';
              }}
            >
              Esqueci meu e-mail
            </button>
          </div>
        </div>
        {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 12,
            background: 'rgba(239, 68, 68, 0.10)',
            color: '#fecaca',
            border: '1px solid rgba(239, 68, 68, 0.22)',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
            {error}
          </div>
        )}
      </div>
      {/*#endregion AC_BLOCK login.email-step*/}
    </>
  );
}
